export const posts = [
  {
    slug: "etsy-seo-spreadsheets",
    title: "How disorganized spreadsheets are killing your Etsy SEO (And how to fix it)",
    date: "2024-05-22",
    excerpt: "Your workflow is leaking money. Standard spreadsheets fail high-volume sellers. Here is the 3-part system you need to stop launching blindly.",
    content: `
# How disorganized spreadsheets are killing your Etsy SEO (And how to fix it)

You research 50 great long-tail keywords. You drop them in Apple Notes. Two weeks later, you publish the listing and use maybe 3 of them. 

Your workflow is leaking money.

Standard spreadsheets fail high-volume sellers. They lack visual pipelines. They separate your keyword data from the actual listing draft.

Here is the 3-part system you need to build immediately to stop launching blindly.

## Step 1: The Keyword Vault
Map out Search Volume versus Competition in a dedicated database. Never lose a profitable niche again.

## Step 2: The Production Pipeline
Use a Kanban board. Move products from "Idea" to "Drafting" to "Published." This reduces launch friction instantly.

## Step 3: The 13-Tag Blueprint
Finalize your title, description, and all 13 tags *before* opening the Etsy dashboard.

## The Shortcut
You can spend the next 5 hours building this system from scratch in Notion. Or you can steal my exact setup instantly.

**The Etsy Command Center Notion Template gives you:**
* Built-in SEO Keyword Vault
* Automated Listing Blueprint Template
* A zero-friction Kanban board
* Lifetime access and updates
    `
  },
  {
    slug: "bypassing-adhd-paralysis",
    title: "Bypassing ADHD Paralysis: The Science of 'Starting'",
    date: "2024-05-20",
    excerpt: "Why our brains freeze before simple tasks and the exact 3-step framework to break the cycle without relying on willpower.",
    content: `
# Bypassing ADHD Paralysis

ADHD paralysis isn't laziness; it's executive dysfunction. When your brain sees a task as a giant, undifferentiated blob of "work," it triggers a fight-or-flight response. Since you can't fight the laundry, you freeze.

## The Dopamine Gap

ADHD brains have a lower baseline of dopamine. We don't just "want" stimulation; we require it to bridge the gap between intention and action. Without enough dopamine, the friction of starting a task feels physically painful.

### The 3-Step Framework

1.  **Micro-Chunking**: Don't "write a blog post." "Open the blank document." That is the only goal.
2.  **External Pressure**: Use a timer (the Pomodoro technique) to create artificial urgency.
3.  **Body Doubling**: Work in the presence of someone else, even virtually.

Starting is the hardest part. Once the momentum begins, the ADHD brain often transitions into hyperfocus, making us more productive than neurotypical peers—if we can just get past the freeze.
    `
  },
  {
    slug: "minimalist-stack-for-creators",
    title: "The Minimalist Stack: Why More Tools = More Chaos",
    date: "2024-05-18",
    excerpt: "The psychological cost of switching tools and why I reduced my entire production workflow to just three essential apps.",
    content: `
# The Minimalist Stack

We are told we need a CRM, a task manager, a calendar, a note-taker, a whiteboarding tool, and a habit tracker. For the ADHD creator, this is a recipe for disaster.

## The Cost of Context Switching

Every time you switch apps, you pay a "context switching tax." Your brain takes up to 20 minutes to fully regain focus on the original task. If you use 10 apps, you spend your entire day paying taxes.

### My Essential Three

- **Notion**: The source of truth.
- **TidyCal**: The gatekeeper.
- **Greta**: The deployment engine.

Anything else is a distraction. If a tool doesn't solve a problem that is costing you money or sanity today, delete it.
    `
  },
  {
    slug: "brutalist-design-productivity",
    title: "Brutalist Design and Productivity: Why Visual Noise Kills Focus",
    date: "2024-05-15",
    excerpt: "How high-contrast, minimal interfaces reduce cognitive load and help ADHD brains stay on track.",
    content: `
# Brutalist Design and Productivity

Why is AlienProduction so black and white? It's not just an aesthetic choice. It's a cognitive one. 

## Reducing Cognitive Load

Colors, shadows, and complex gradients are data points that your brain has to process. For a neurotypical person, this is background noise. For someone with ADHD, it's a thousand tiny needles pulling at their attention.

### High Contrast, High Clarity

By using a brutalist, high-contrast palette:
1. We establish a clear hierarchy.
2. We remove "visual friction."
3. We focus on the only thing that matters: the content.

When you remove the noise, you reveal the signal. Focus is the ultimate luxury in a world designed to distract you.
    `
  }
];